import m from 'mithril'
import type { Me } from '../../shared/types.ts'
import { api, apiBase } from '../api.ts'
import { logout, state } from '../state.ts'
import { ErrorNote } from '../ui.ts'

export const SettingsView: m.ClosureComponent = () => {
  let displayName = state.me?.displayName ?? ''
  let username = state.me?.username ?? ''
  let bio = state.me?.bio ?? ''
  let isPrivate = state.me?.isPrivate ?? false
  let error = ''
  let saved = false
  let busy = false

  const apply = (me: Me) => {
    state.me = me
    displayName = me.displayName
    username = me.username
    bio = me.bio
    isPrivate = me.isPrivate
  }

  const save = async (event: Event) => {
    event.preventDefault()
    busy = true
    error = ''
    try {
      apply(await api<Me>('/api/me', { method: 'PATCH', body: { displayName, username, bio, isPrivate } }))
      saved = true
    } catch (err) {
      error = (err as Error).message
    } finally {
      busy = false
      m.redraw()
    }
  }

  const rotate = async () => {
    busy = true
    try {
      apply(await api<Me>('/api/me/feed-token', { method: 'POST' }))
    } catch (err) {
      error = (err as Error).message
    } finally {
      busy = false
      m.redraw()
    }
  }

  const feedRow = (label: string, url: string, note?: string) =>
    m('div.feed-row', [
      m('span.feed-label', label),
      m('code.feed-url', url),
      m('div.row', [
        m('a.button.small', { href: url, target: '_blank', rel: 'noopener' }, 'Open'),
        m(
          'button.button.small',
          { type: 'button', onclick: () => navigator.clipboard?.writeText(url) },
          'Copy',
        ),
      ]),
      note ? m('p.muted', note) : null,
    ])

  return {
    view: () => {
      const me = state.me
      if (!me) {
        return m('section.stack', [
          m('p', 'You are signed out.'),
          m(m.route.Link, { href: '/login', class: 'button primary' }, 'Sign in'),
        ])
      }

      const origin = apiBase || location.origin

      return m('section.stack', [
        m('form.card.stack', { onsubmit: save }, [
          m('label', [
            'Display name',
            m('input', {
              value: displayName,
              oninput: (event: Event) => {
                displayName = (event.target as HTMLInputElement).value
              },
            }),
          ]),
          m('label', [
            'Username',
            m('input', {
              value: username,
              autocapitalize: 'none',
              oninput: (event: Event) => {
                username = (event.target as HTMLInputElement).value
              },
            }),
          ]),
          m('label', [
            'Bio',
            m('textarea', {
              rows: 3,
              value: bio,
              oninput: (event: Event) => {
                bio = (event.target as HTMLTextAreaElement).value
              },
            }),
          ]),
          m('label.checkbox', [
            m('input', {
              type: 'checkbox',
              checked: isPrivate,
              onchange: (event: Event) => {
                isPrivate = (event.target as HTMLInputElement).checked
              },
            }),
            m('span', [
              m('strong', 'Private account'),
              m('span.muted', 'Only you see your reviews'),
            ]),
          ]),
          error ? m(ErrorNote, { text: error }) : null,
          saved && !error ? m('p.saved', 'Saved.') : null,
          m('button.button.primary', { type: 'submit', disabled: busy }, 'Save'),
        ]),

        m('section.card.stack', [
          m('h2', 'Feeds'),
          me.isPrivate
            ? m('p.muted', 'Your public feed is off while your account is private.')
            : feedRow('Public', `${origin}/feed/u/${me.username}.xml`),
          feedRow(
            'Private',
            `${origin}/feed/t/${me.feedToken}.xml`,
            'Share only with people you trust',
          ),
          m('button.button.small', { type: 'button', onclick: rotate, disabled: busy }, 'Rotate private link'),
        ]),

        m('section.card.stack', [
          m('h2', 'Account'),
          m('button.button.danger', { type: 'button', onclick: logout }, 'Sign out'),
        ]),
      ])
    },
  }
}
